import { memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { AppContext } from '../../../../App'
import { Box } from '@mui/material'
import { getSecurityAsync } from '../../../../util/security'
import { MultiStrikeSecuritiesClassType } from '../../../../reducer/multiStrikeChart.reducer'
import MultiStrikeMetadataHeader from './MultiStrikeMetadataHeader'
import MultiStrikeMetadataStrikeList from './MultiStrikeMetadataStrikeList'
import {
    bumpMultiStrikeCrosshairRevision,
    setMultiStrikeChartCrosshairSnapshot,
    setMultiStrikeChartLiveSnapshot,
} from '../../multiStrikeChartSnapshotStore'
import { buildLatestMultiStrikeStringSnapshot } from '../../buildMultiStrikeStringSnapshot'

const METADATA_LEFT_PADDING_PX = 6

export type ChartMetadataProps = {
    container: HTMLDivElement
    viewKey: string
    multiStrikeClassType: MultiStrikeSecuritiesClassType
    leftScaleWidth?: number
    displayCumulativeToggle?: boolean
    onSecuritySelected?: (key: string, expressionMode: boolean, securities: string[]) => void
}

const ChartMetadata = ({
    container,
    viewKey,
    multiStrikeClassType,
    leftScaleWidth = 0,
    displayCumulativeToggle = true,
    onSecuritySelected,
}: ChartMetadataProps) => {
    const [paneContainer, setPaneContainer] = useState<HTMLDivElement>()
    const appContext = useContext(AppContext)
    const chart = appContext.getMultiStrikeChart(viewKey)
    const [primarySecurity, setPrimarySecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const primarySecKey = chart?.primary ?? ''

    const metadataLeft = useMemo(
        () => `${(leftScaleWidth > 0 ? leftScaleWidth : 0) + METADATA_LEFT_PADDING_PX}px`,
        [leftScaleWidth]
    )

    useEffect(() => {
        const k = chart?.primary ?? ''
        if (k) getSecurityAsync(k).then(setPrimarySecurity)
    }, [chart?.primary])

    const updatePaneContainer = useCallback(() => {
        if (!container?.isConnected) {
            setPaneContainer(undefined)
            return
        }
        let child = container.querySelector<HTMLDivElement>(`.chart-pane-container`)
        if (child) {
            setPaneContainer(child)
        } else {
            const newChild = document.createElement('div')
            newChild.classList.add('chart-pane-container')
            const containerStyle: Record<string, string> = {
                display: 'flex',
                'flex-direction': 'column',
                'z-index': '3',
                'justify-content': 'start',
                'align-items': 'start',
                width: '100%',
            }

            for (const key in containerStyle) {
                newChild.style.setProperty(key, containerStyle[key]!)
            }
            container.appendChild(newChild)
            setPaneContainer(newChild)
        }
    }, [container])

    useLayoutEffect(() => {
        updatePaneContainer()
    }, [container, updatePaneContainer])

    const showLatestCandleInMetadata = useCallback(() => {
        setMultiStrikeChartCrosshairSnapshot(viewKey, null)
        const chartInstance = appContext.getMultiStrikeChart(viewKey)
        if (chartInstance) {
            setMultiStrikeChartLiveSnapshot(viewKey, buildLatestMultiStrikeStringSnapshot(chartInstance))
        }
        bumpMultiStrikeCrosshairRevision(viewKey)
    }, [appContext, viewKey])

    const header = useMemo(
        () => (
            <MultiStrikeMetadataHeader
                viewKey={viewKey}
                security={primarySecurity}
                primarySecKey={primarySecKey}
                displayCumulativeToggle={displayCumulativeToggle}
                onSecuritySelected={onSecuritySelected}
            />
        ),
        [viewKey, primarySecurity, primarySecKey, displayCumulativeToggle, onSecuritySelected]
    )

    const strikeList = useMemo(
        () => (
            <MultiStrikeMetadataStrikeList viewKey={viewKey} multiStrikeClassType={multiStrikeClassType} />
        ),
        [viewKey, multiStrikeClassType]
    )

    return (
        <>
            {paneContainer &&
                createPortal(
                    <Box
                        className="pane-meta-container disable-selection"
                        onMouseEnter={showLatestCandleInMetadata}
                        sx={{
                            position: 'absolute',
                            left: metadataLeft,
                            pointerEvents: 'none',
                            top: 0,
                            boxSizing: 'border-box',
                            maxWidth: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        {header}
                        {strikeList}
                    </Box>,
                    paneContainer
                )}
        </>
    )
}

export { ChartMetadata }
export default memo(ChartMetadata, (prev, next) => {
    if (
        prev.viewKey !== next.viewKey ||
        prev.container !== next.container ||
        prev.leftScaleWidth !== next.leftScaleWidth ||
        prev.displayCumulativeToggle !== next.displayCumulativeToggle
    ) {
        return false
    }
    const prevStrikes = Object.keys(prev.multiStrikeClassType ?? {})
    const nextStrikes = Object.keys(next.multiStrikeClassType ?? {})
    if (prevStrikes.length !== nextStrikes.length) return false
    for (let i = 0; i < prevStrikes.length; i++) {
        const strike = prevStrikes[i]
        const prevEntry = prev.multiStrikeClassType[strike]
        const nextEntry = next.multiStrikeClassType[strike]
        if (
            !nextEntry ||
            prevEntry.callSecKey !== nextEntry.callSecKey ||
            prevEntry.putSecKey !== nextEntry.putSecKey
        ) {
            return false
        }
    }
    return true
})
